import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { TileLayer } from "react-leaflet";

import MapActioner from "services/map/actioner";
import MapUtils from "services/map/utils";
import PedestrianStatisticsActioner from "services/pedestrianStatistics/actioner";
import MapConfig from "services/map/MapConfig";

import AreasLayers from "components/AreasLayers";
import LeafletMap from "components/LeafletMap";

function MapComponent({
  center,
  initFloorsData,
  maxZoom,
  minZoom,
  onRecenter,
  pedestrianStatisticsMap,
  pedestrianStatisticsDetails,
  retrieveLayersInfos,
  setConfigParam,
  setSelectedLayers,
  tilesUrl,
  zoom,
}) {
  const [leafletMap, setLeafletMap] = useState();

  useEffect(() => {
    async function initializeData() {
      if (pedestrianStatisticsMap?.initialized) {
        initFloorsData();
        return null;
      }

      await initFloorsData();
      await retrieveLayersInfos();
    }

    initializeData();
  }, [
    pedestrianStatisticsMap?.initialized,
    initFloorsData,
    retrieveLayersInfos,
  ]);

  useEffect(() => {
    if (leafletMap) {
      onRecenter(leafletMap?.leafletElement);
    }
  }, [onRecenter, leafletMap]);

  function getMapRadius(currentMap) {
    return MapUtils.getRadiusInMeters(
      currentMap.getBounds().getNorthEast(),
      currentMap.getCenter()
    );
  }

  function handleZoomEnd(e) {
    const currentMap = e?.target;
    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentZoom", currentMap.getZoom());
  }

  function handleMoveEnd(e) {
    const currentMap = e?.target;

    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentPosition", currentMap.getCenter());
  }

  function handleOnLoad(e) {
    const currentMap = e?.target;
    setConfigParam("currentRadius", getMapRadius(currentMap._map));
    setConfigParam("currentPosition", currentMap._map.getCenter());
  }

  function handleGetLayerLabel(layer) {
    if (layer.type === "point" || layer.type === "area") {
      return layer?.short_name || layer?.long_name || null;
    }

    return null;
  }

  function handleGetAreaLabelIcon(layer) {
    return MapConfig?.iconByType?.pedestrian?.[layer.type]?.[layer.apiType];
  }

  return (
    <LeafletMap
      setMapRef={setLeafletMap}
      preferCanvas
      style={{
        width: "100%",
        height: "100%",
      }}
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      onZoomEnd={handleZoomEnd}
      onMoveEnd={handleMoveEnd}
    >
      <TileLayer url={MapConfig.defaultTileUrl} />
      <TileLayer
        url={tilesUrl}
        onLoad={handleOnLoad}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />
      <AreasLayers
        map={pedestrianStatisticsMap}
        options={pedestrianStatisticsDetails}
        onSelectLayers={setSelectedLayers}
        getLabel={handleGetLayerLabel}
        getLabelIcon={handleGetAreaLabelIcon}
      />
    </LeafletMap>
  );
}

const mapStateToProps = (state) => ({
  center: MapUtils.getCenter(state.Map),
  pedestrianStatisticsMap: state.PedestrianStatistics.map,
  pedestrianStatisticsDetails: state.PedestrianStatistics.details,
  maxZoom: MapUtils.getMaxZoom(state.Map),
  minZoom: MapUtils.getMinZoom(state.Map),
  tilesUrl: MapUtils.getTilesUrl(state.Map),
  zoom: MapUtils.getZoom(state.Map),
});

const mapDispatchToProps = (dispatch) => ({
  retrieveLayersInfos: () =>
    dispatch(PedestrianStatisticsActioner.retrieveLayersInfos()),
  retrieveAllPedestrianStats: () =>
    dispatch(PedestrianStatisticsActioner.retrieveAllStats()),
  setConfigParam: (key, value) =>
    dispatch(MapActioner.setConfigParam(key, value)),
  initFloorsData: () =>
    dispatch(MapActioner.initFloorsDataFromModule("pedestrian_stats")),
  setSelectedLayers: (layers) =>
    dispatch(PedestrianStatisticsActioner.setSelectedLayers(layers)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MapComponent);
